import { cn } from '@/lib/utils';
import { EmailData } from './email-viewer-types';
import { Card, CardContent } from '@/components/ui/card';
import DOMPurify from 'dompurify';
import { buildCidMap } from './email-thread-utils';

const renderedEmailOverrides = cn(
  '[&_img]:max-w-full',
  '[&_a]:text-blue-600',
  '[&_p]:mb-4 [&_p]:leading-6 [&_p]:text-sm [&_p]:text-gray-900',
  '[&_h1]:mb-2 [&_h1]:text-gray-900',
  '[&_h2]:mb-2 [&_h2]:text-gray-900',
  '[&_h3]:mb-2 [&_h3]:text-gray-900',
  '[&_h4]:mb-2 [&_h4]:text-gray-900',
  '[&_h5]:mb-2 [&_h5]:text-gray-900',
  '[&_h6]:mb-2 [&_h6]:text-gray-900',
  '[&_ul]:pl-6 [&_ul]:mb-4',
  '[&_ol]:pl-6 [&_ol]:mb-4',
  '[&_blockquote]:border-l-4 [&_blockquote]:border-gray-300 [&_blockquote]:pl-4 [&_blockquote]:ml-0 [&_blockquote]:mb-4 [&_blockquote]:italic',
  '[&_pre]:bg-gray-50 [&_pre]:p-4 [&_pre]:rounded [&_pre]:overflow-x-auto [&_pre]:mb-4',
  '[&_code]:bg-gray-50 [&_code]:px-2 [&_code]:py-1 [&_code]:rounded [&_code]:text-sm',
);

/**
 * Replaces cid: URLs in HTML content with actual signed URLs
 * @param content - HTML content with potential cid: references
 * @param cidMap - Map of content IDs to signed URLs
 * @returns HTML content with cid: URLs replaced
 */
function replaceCidUrls(content: string, cidMap: Map<string, string>): string {
  if (cidMap.size === 0) return content;

  // Replace cid: references in src attributes
  // Handles both cid:image001.png and cid:image001.png@01234567.ABCDEF formats
  return content.replace(
    /src=["']cid:([^"'@>]+)(?:@[^"'>]*)?\s*["']/gi,
    (match, contentId) => {
      const url = cidMap.get(contentId);
      if (url) {
        return `src="${url}"`;
      }
      // Return original if no mapping found
      return match;
    },
  );
}

export function EmailBody({ emailData }: { emailData: EmailData }) {
  const htmlContent = emailData.htmlContent;
  const textContent = emailData.textContent;

  // Check if we have any content at all
  const hasHtmlContent =
    htmlContent &&
    htmlContent
      .replace(/<[^>]*>/g, '') // Remove HTML tags
      .replace(/&nbsp;/g, ' ') // Replace &nbsp; with space
      .trim().length > 0;

  const hasTextContent = textContent && textContent.trim().length > 0;

  const hasAnyContent = hasHtmlContent || hasTextContent;

  if (!hasAnyContent) {
    return (
      <Card>
        <CardContent className="flex justify-center items-center">
          <span className="text-xs text-gray-400 italic tracking-wide opacity-20">
            No email body content
          </span>
        </CardContent>
      </Card>
    );
  }

  // Render HTML content if available
  if (hasHtmlContent) {
    // Build CID map for inline images
    const cidMap = buildCidMap(emailData.documentAssociations);

    // Replace cid: URLs with actual signed URLs before sanitizing
    const contentWithResolvedCids = replaceCidUrls(htmlContent, cidMap);

    // Sanitize HTML content to prevent XSS attacks
    const sanitizedContent = DOMPurify.sanitize(contentWithResolvedCids, {
      ALLOWED_TAGS: [
        'p',
        'br',
        'strong',
        'em',
        'u',
        'h1',
        'h2',
        'h3',
        'h4',
        'h5',
        'h6',
        'ul',
        'ol',
        'li',
        'a',
        'img',
        'blockquote',
        'pre',
        'code',
        'div',
        'span',
        'table',
        'thead',
        'tbody',
        'tr',
        'th',
        'td',
      ],
      ALLOWED_ATTR: ['href', 'src', 'alt', 'title', 'class', 'id', 'style'],
      ALLOW_DATA_ATTR: false,
    });

    return (
      <Card>
        <CardContent
          className={cn(
            'bg-white prose prose-sm max-w-none',
            renderedEmailOverrides,
          )}
        >
          <div
            dangerouslySetInnerHTML={{
              __html: sanitizedContent,
            }}
          />
        </CardContent>
      </Card>
    );
  }

  // Fallback to text content with proper line break handling
  return (
    <Card>
      <CardContent className="bg-white">
        <div className="text-sm text-gray-900 leading-6 whitespace-pre-wrap break-words">
          {textContent}
        </div>
      </CardContent>
    </Card>
  );
}
